/**
 * Visual analysis card for image creatives
 * Shows composition, text elements, and color analysis
 */

import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/utils';
import type { VisualAnalysis } from '@/types';

export interface VisualAnalysisCardProps {
  visualAnalysis: VisualAnalysis;
  className?: string;
}

/**
 * Badge component for enum values
 */
function Badge({
  label,
  variant = 'default',
}: {
  label: string;
  variant?: 'default' | 'success' | 'warning' | 'danger';
}) {
  const variantClasses = {
    default: 'bg-gray-100 text-gray-700',
    success: 'bg-green-100 text-green-700',
    warning: 'bg-yellow-100 text-yellow-700',
    danger: 'bg-red-100 text-red-700',
  };

  return (
    <span
      className={cn(
        'px-2 py-0.5 text-xs font-medium rounded',
        variantClasses[variant]
      )}
    >
      {label}
    </span>
  );
}

/**
 * Get badge variant for balance values
 */
function getBalanceVariant(
  balance: string
): 'default' | 'success' | 'warning' {
  if (balance === 'balanced') return 'success';
  return 'warning';
}

/**
 * Get badge variant for whitespace values
 */
function getWhitespaceVariant(
  whitespace: string
): 'default' | 'success' | 'warning' | 'danger' {
  if (whitespace === 'adequate') return 'success';
  if (whitespace === 'cramped') return 'danger';
  return 'warning';
}

/**
 * Get badge variant for readability values
 */
function getReadabilityVariant(
  readability: string
): 'success' | 'warning' | 'danger' {
  if (readability === 'clear') return 'success';
  if (readability === 'marginal') return 'warning';
  return 'danger';
}

/**
 * Get badge variant for contrast values
 */
function getContrastVariant(
  contrast: string
): 'success' | 'warning' | 'danger' {
  if (contrast === 'high') return 'success';
  if (contrast === 'medium') return 'warning';
  return 'danger';
}

export function VisualAnalysisCard({
  visualAnalysis,
  className,
}: VisualAnalysisCardProps) {
  const { composition, textElements, colorAnalysis } = visualAnalysis;

  return (
    <Card className={cn('p-6', className)}>
      <h3 className="text-lg font-bold text-gray-900 mb-6">Visual Analysis</h3>

      <div className="space-y-8">
        {/* Composition Section */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
              />
            </svg>
            Composition
          </h4>

          <div className="space-y-3">
            <div className="flex items-start justify-between">
              <span className="text-sm text-gray-600">Focal Point</span>
              <span className="text-sm font-medium text-gray-900 text-right max-w-[60%]">
                {composition.focalPoint}
              </span>
            </div>
            <div className="flex items-start justify-between">
              <span className="text-sm text-gray-600">Visual Flow</span>
              <span className="text-sm font-medium text-gray-900 text-right max-w-[60%]">
                {composition.visualFlow}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Balance</span>
              <Badge
                label={composition.balance}
                variant={getBalanceVariant(composition.balance)}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Whitespace</span>
              <Badge
                label={composition.whitespaceUsage}
                variant={getWhitespaceVariant(composition.whitespaceUsage)}
              />
            </div>
          </div>
        </div>

        {/* Text Elements Section */}
        {textElements.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Text Elements
              <span className="text-xs font-normal text-gray-400">
                ({textElements.length})
              </span>
            </h4>

            <div className="space-y-3">
              {textElements.map((element, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-3"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge label={element.role} />
                      <Badge
                        label={element.readability}
                        variant={getReadabilityVariant(element.readability)}
                      />
                    </div>
                    <span className="text-xs text-gray-500">
                      {element.estimatedTextAreaPercent.toFixed(0)}% area
                    </span>
                  </div>
                  <p className="text-sm text-gray-900 font-medium mb-1">
                    &ldquo;{element.content}&rdquo;
                  </p>
                  <p className="text-xs text-gray-500">{element.location}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Color Analysis Section */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
              />
            </svg>
            Color Analysis
          </h4>

          <div className="space-y-3">
            {/* Dominant Colors */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Dominant Colors</span>
              <div className="flex items-center gap-1.5">
                {colorAnalysis.dominantColors.map((color, index) => (
                  <div
                    key={index}
                    className="w-6 h-6 rounded-full border border-gray-200"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>

            {/* Brand Color Match */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Brand Color Match</span>
              <span
                className={cn(
                  'px-3 py-1 rounded-full text-xs font-bold uppercase',
                  colorAnalysis.brandColorMatch
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                )}
              >
                {colorAnalysis.brandColorMatch ? 'Yes' : 'No'}
              </span>
            </div>

            {/* Contrast */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Contrast</span>
              <Badge
                label={colorAnalysis.contrast}
                variant={getContrastVariant(colorAnalysis.contrast)}
              />
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
